import type { ConfigStoreState } from '@/stores/config.ts';
import type { EnvironmentConfig } from '@/stores/config/config.type.ts';
import { useNewVersionStore } from '@/stores/newVersion.ts';

/** How often a visible page asks `/config` which version the server runs. */
const CHECK_INTERVAL_MS = 5 * 60_000;

// Local debugging: logs each /config check with the time, to follow the checks in the console.
const logCheck = (message: string) =>
  console.info(`[/config] ${new Date().toLocaleTimeString()} ${message}`);

/** The version in an image tag, `<sha>-v<version>-<env>`: `0.2.886` in `13f748d0…-v0.2.886-dev`. */
const versionOf = (imageTag: string | undefined) =>
  imageTag?.match(/(?:^|-)v(\d+\.\d+\.\d+)(?:-|$)/)?.[1];

/**
 * Sets `useNewVersionStore` while `/config` reports another version in `image_tag` than the one this
 * page loaded with, so the top bar can offer a reload. Nothing reloads on its own. Only the version
 * counts, not the commit or the environment in the tag.
 *
 * Checks every five minutes while the page is visible, when it becomes visible again, and right away
 * when a lazy route file fails to load. That is what an open page runs into once a deploy has removed
 * its old files, but a flaky network does it too, so the failed load alone is not taken as a deploy.
 * It keeps checking once a new version shows, so the top bar follows a later deploy or a rollback.
 *
 * Call once, after `initConfigStore()` has settled. Does nothing if `/config` failed or its
 * `image_tag` holds no version.
 */
export const watchForNewVersion = ({
  imageTag,
  error,
}: Pick<ConfigStoreState, 'imageTag' | 'error'>) => {
  const runningVersion = versionOf(imageTag);
  if (error || !runningVersion) {
    return;
  }
  useNewVersionStore.setState({ runningVersion });

  // One check at a time: several route files can fail to load at once, and an older response that
  // lands after a newer one would otherwise set the store back.
  let inFlight: Promise<void> | undefined;
  const check = () => {
    inFlight ??= checkForNewVersion(runningVersion).finally(() => {
      inFlight = undefined;
    });
  };
  const checkIfVisible = () => {
    if (document.visibilityState === 'visible') {
      check();
    } else {
      logCheck(`check skipped, the page is ${document.visibilityState}`);
    }
  };

  setInterval(checkIfVisible, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', checkIfVisible);
  window.addEventListener('vite:preloadError', check);
};

const checkForNewVersion = async (runningVersion: string) => {
  try {
    const response = await fetch(`${window.origin}/config`, { cache: 'no-store' });
    if (!response.ok) {
      logCheck(`read failed: ${response.status}`);
      return;
    }
    const { image_tag: imageTag } = (await response.json()) as EnvironmentConfig;
    const serverVersion = versionOf(imageTag);
    logCheck(`read: version ${serverVersion} (image_tag ${imageTag}), page runs ${runningVersion}`);
    if (serverVersion) {
      // A rollback to the version this page runs clears it again.
      useNewVersionStore.setState({
        newVersion: serverVersion === runningVersion ? undefined : serverVersion,
      });
    }
  } catch (err) {
    logCheck(`read failed: ${String(err)}`);
    // Offline, or the server is restarting for the deploy: the next check tries again.
  }
};
